import React, { useState, useEffect } from 'react';
import useFontFamilies from '../../hooks/useFontFamilies';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Slider,
  IconButton,
  Tooltip,
  FormControlLabel,
  Checkbox,
  Divider,
} from '@mui/material';
import {
  TextFields,
  FormatAlignLeft,
  FormatAlignCenter,
  FormatAlignRight,
  Save,
  Search,
  Opacity,
  Visibility,
  VisibilityOff,
} from '@mui/icons-material';
import { MuiColorInput } from 'mui-color-input';
import SearchButtonSettings from './SearchButtonSettings';
import { usePxToVw } from '../../hooks/usePxToVw';

const MainTextPage = ({
  userId,
  buttonColor,
  sectionType = 'category',
  title = 'Main Category Text Settings',
  sections,
  loading,
  onSectionChange,
  onToggleSection,
  onSearchConfigChange,
  onSave,
}) => {
  const { pxToVw } = usePxToVw();
  const fontOptions = useFontFamilies('option');


  const renderPreview = (sectionKey, sectionData, label) => {
    const style = {
      color: sectionData.color,
      fontSize: pxToVw(`${sectionData.fontSize}`),
      fontWeight: sectionData.fontWeight,
      textAlign: sectionData.textAlign,
      opacity: sectionData.opacity,
      textShadow: `${sectionData.textShadowH}px ${sectionData.textShadowV}px ${sectionData.textShadowBlur}px ${sectionData.textShadowColor}`,
      position: 'relative',
      padding: '10px',
      backgroundColor: 'rgba(255, 255, 255, 0.1)',
      borderRadius: '4px',
      maxWidth: '100%',
      wordWrap: 'break-word',
      whiteSpace: 'pre-wrap',
      // marginTop: pxToVw(sectionData.marginTop || 0),
      // marginBottom: pxToVw(sectionData.marginBottom || 0),
      textAlign: sectionData.textAlign,
      fontFamily: sectionData.fontFamily || 'Arial, sans-serif',
    };

    // Determine if left/right position should be disabled based on text alignment
    const isLeftPositionDisabled =
      sectionData.textAlign === 'center' || sectionData.textAlign === 'right';
    const isRightPositionDisabled =
      sectionData.textAlign === 'center' || sectionData.textAlign === 'left';

    return (
      <div className={`form-group   ${!sectionData.enabled ? 'disabled' : ''}`}>
        <div className="form-group" style={{ marginBottom: '15px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <TextFields />
              {label}
            </Typography>
            <FormControlLabel
              control={
                <Checkbox
                  checked={sectionData.enabled}
                  onChange={() => onToggleSection(sectionKey)}
                  icon={<VisibilityOff />}
                  checkedIcon={<Visibility />}
                  sx={{ '&.Mui-checked': { color: buttonColor } }}
                />
              }
              label={sectionData.enabled ? 'Enabled' : 'Disabled'}
            />
          </div>
        </div>

        {sectionData.enabled && (
          <>
            <div className="form-group">
              <label className="form-label" style={{ fontWeight: '600', marginBottom: '10px' }}>
                Preview
              </label>
              <div
                style={{
                  backgroundColor: '#f5f5f5',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  padding: '20px',
                  minHeight: '100px',
                  position: 'relative',
                }}
              >
                <div style={style}>
                  {sectionData.text || `Your ${label.toLowerCase()} text will appear here`}
                </div>
                <div
                  style={{
                    position: 'absolute',
                    bottom: '5px',
                    right: '5px',
                    fontSize: '12px',
                    color: '#666',
                  }}
                >
                  Preview
                </div>
              </div>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
              {/* Text Content */}
              <div className="form-group">
                <div className="row menu-card2">
                  <div className="col-md-12">
                    <label>Text Content</label>
                    <textarea
                      className="form-control"
                      value={sectionData.text || ''}
                      onChange={e => onSectionChange(sectionKey, 'text', e.target.value)}
                      rows={3}
                      placeholder={`Enter ${label.toLowerCase()} text...`}
                    />
                  </div>
                </div>
              </div>

              {/* Color and Opacity */}
              <div className="row menu-card2">
                <div className="col-md-6">
                  <div className="form-group">
                    <label>Text Color</label>
                    <MuiColorInput
                      format="hex"
                      value={sectionData.color || '#000000'}
                      onChange={value => onSectionChange(sectionKey, 'color', value)}
                      className="form-control"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: '0px',
                          height: 53,
                        },
                        '& .MuiOutlinedInput-input': {
                          height: '36px',
                        },
                      }}
                    />
                  </div>
                </div>

                <div className="col-md-6">
                  <div className="form-group">
                    <label>Opacity: {Math.round((sectionData.opacity || 1) * 100)}%</label>
                    <div className="slider-container">
                      <Slider
                        value={sectionData.opacity || 1}
                        onChange={(e, value) => onSectionChange(sectionKey, 'opacity', value)}
                        min={0.1}
                        max={1}
                        step={0.1}
                        valueLabelDisplay="auto"
                        sx={{ color: buttonColor }}
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Font Size and Text Alignment */}
              <div className="row menu-card2">
                <div className="col-md-6">
                  <div className="form-group">
                    <label>Font Size (px)</label>
                    <input
                      type="number"
                      className="form-control"
                      value={sectionData.fontSize || 16}
                      onChange={e =>
                        onSectionChange(sectionKey, 'fontSize', parseInt(e.target.value) || 16)
                      }
                    />
                  </div>
                </div>

                <div className="col-md-6">
                  <div className="form-group">
                    <label>Text Alignment</label>
                    <div style={{ display: 'flex', gap: '10px', marginTop: '5px' }}>
                      <button
                        type="button"
                        className={`btn ${sectionData.textAlign === 'left' ? 'active' : ''}`}
                        onClick={() => onSectionChange(sectionKey, 'textAlign', 'left')}
                        style={{
                          backgroundColor:
                            sectionData.textAlign === 'left' ? buttonColor : '#f8f9fa',
                          color: sectionData.textAlign === 'left' ? 'white' : '#212529',
                          border: '1px solid #dee2e6',
                          padding: '8px 16px',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '5px',
                          flex: 1,
                          justifyContent: 'center',
                        }}
                      >
                        <FormatAlignLeft /> Left
                      </button>
                      <button
                        type="button"
                        className={`btn ${sectionData.textAlign === 'center' ? 'active' : ''}`}
                        onClick={() => onSectionChange(sectionKey, 'textAlign', 'center')}
                        style={{
                          backgroundColor:
                            sectionData.textAlign === 'center' ? buttonColor : '#f8f9fa',
                          color: sectionData.textAlign === 'center' ? 'white' : '#212529',
                          border: '1px solid #dee2e6',
                          padding: '8px 16px',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '5px',
                          flex: 1,
                          justifyContent: 'center',
                        }}
                      >
                        <FormatAlignCenter /> Center
                      </button>
                      <button
                        type="button"
                        className={`btn ${sectionData.textAlign === 'right' ? 'active' : ''}`}
                        onClick={() => onSectionChange(sectionKey, 'textAlign', 'right')}
                        style={{
                          backgroundColor:
                            sectionData.textAlign === 'right' ? buttonColor : '#f8f9fa',
                          color: sectionData.textAlign === 'right' ? 'white' : '#212529',
                          border: '1px solid #dee2e6',
                          padding: '8px 16px',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '5px',
                          flex: 1,
                          justifyContent: 'center',
                        }}
                      >
                        <FormatAlignRight /> Right
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              {/* Font Weight and Font Family */}
              <div className="row menu-card2">
                <div className="col-md-6">
                  <div className="form-group">
                    <label>Font Weight</label>
                    <select
                      className="form-control"
                      value={sectionData.fontWeight || 400}
                      onChange={e =>
                        onSectionChange(sectionKey, 'fontWeight', parseInt(e.target.value))
                      }
                    >
                      <option value="100">100 (Thin)</option>
                      <option value="200">200 (Extra Light)</option>
                      <option value="300">300 (Light)</option>
                      <option value="400">400 (Normal)</option>
                      <option value="500">500 (Medium)</option>
                      <option value="600">600 (Semi Bold)</option>
                      <option value="700">700 (Bold)</option>
                      <option value="800">800 (Extra Bold)</option>
                      <option value="900">900 (Black)</option>
                    </select>
                  </div>
                </div>

                <div className="col-md-6">
                  <div className="form-group">
                    <label>Font Family</label>
                    <select
                      className="form-control"
                      value={sectionData.fontFamily}
                      onChange={e => onSectionChange(sectionKey, 'fontFamily', e.target.value)}
                      style={{
                        width: '100%',
                        padding: '12px',
                        border: '1px solid #ddd',
                        borderRadius: '8px',
                        fontSize: '0.875rem',
                      }}
                    >
                      {fontOptions}
                    </select>
                  </div>
                </div>
              </div>

              {/* Text Shadow */}
              <Divider style={{ margin: '15px 0' }} />
              <div className="form-group">
                <label className="form-label" style={{ fontWeight: '600', marginBottom: '10px' }}>
                  Text Shadow
                </label>
                <div className="row menu-card2">
                  <div className="col-md-3">
                    <div className="form-group">
                      <label>Horizontal (px)</label>
                      <input
                        type="number"
                        className="form-control"
                        value={sectionData.textShadowH || '0'}
                        onChange={e => onSectionChange(sectionKey, 'textShadowH', e.target.value)}
                      />
                    </div>
                  </div>
                  <div className="col-md-3">
                    <div className="form-group">
                      <label>Vertical (px)</label>
                      <input
                        type="number"
                        className="form-control"
                        value={sectionData.textShadowV || '0'}
                        onChange={e => onSectionChange(sectionKey, 'textShadowV', e.target.value)}
                      />
                    </div>
                  </div>
                  <div className="col-md-3">
                    <div className="form-group">
                      <label>Blur (px)</label>
                      <input
                        type="number"
                        className="form-control"
                        value={sectionData.textShadowBlur || '0'}
                        onChange={e =>
                          onSectionChange(sectionKey, 'textShadowBlur', e.target.value)
                        }
                      />
                    </div>
                  </div>

                  <div className="col-md-3">
                    <div className="form-group">
                      <label>Shadow Color</label>
                      <MuiColorInput
                        format="hex"
                        value={sectionData.textShadowColor || '#000000'}
                        onChange={value => onSectionChange(sectionKey, 'textShadowColor', value)}
                        className="form-control"
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: '0px',
                            height: 53,
                          },
                          '& .MuiOutlinedInput-input': {
                            height: '36px',
                          },
                        }}
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Margin Settings */}
              <Divider style={{ margin: '15px 0' }} />
              <div className="form-group">
                <label className="form-label" style={{ fontWeight: '600', marginBottom: '10px' }}>
                  Margin Settings
                </label>
                <div className="row menu-card2">
                  <div className="col-md-6">
                    <div className="form-group">
                      <label>Margin Top (px)</label>
                      <input
                        type="number"
                        className="form-control"
                        value={sectionData.marginTop || 0}
                        onChange={e =>
                          onSectionChange(sectionKey, 'marginTop', parseInt(e.target.value) || 0)
                        }
                      />
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="form-group">
                      <label>Margin Bottom (px)</label>
                      <input
                        type="number"
                        className="form-control"
                        value={sectionData.marginBottom || 0}
                        onChange={e =>
                          onSectionChange(sectionKey, 'marginBottom', parseInt(e.target.value) || 0)
                        }
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    );
  };

  return (
    <div>
      <div className="pd-ltr-20 xs-pd-20-10">
        <div className="min-height-200px">
          <div className="dashboard-container">
            <div className="tab-wizard wizard-circle wizard vertical">
              {/* Text Sections */}
              {renderPreview('header', sections.header, 'Title')}
              {renderPreview('description', sections.description, 'Sub Title')}
              {renderPreview('footer', sections.footer, 'Footer Text')}

              {/* Search Config Section */}
              <div className="form-group">
                <div className="form-group" style={{ marginBottom: '15px' }}>
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                    }}
                  >
                    <Typography
                      variant="h6"
                      style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
                    >
                      <Search />
                      Search Button Configuration
                    </Typography>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={sections.searchConfig?.enabled}
                          onChange={() =>
                            onSearchConfigChange('enabled', !sections.searchConfig?.enabled)
                          }
                          icon={<VisibilityOff />}
                          checkedIcon={<Visibility />}
                          sx={{ '&.Mui-checked': { color: buttonColor } }}
                        />
                      }
                      label={sections.searchConfig?.enabled ? 'Enabled' : 'Disabled'}
                    />
                  </div>
                </div>

                {sections.searchConfig?.enabled && (
                  <SearchButtonSettings
                    searchConfig={sections.searchConfig}
                    onSearchConfigChange={newConfig => {
                      onSearchConfigChange('searchConfig', newConfig);
                    }}
                    buttonColor={buttonColor}
                  />
                )}
              </div>
            </div>
          </div>

          <div className="page-header">
            <div
              className="row"
              style={{
                display: 'flex',
                justifyContent: 'flex-end',
              }}
            >
              {/* <div className="col-md-6 col-sm-12">
                                <div className="title">
                                    <Typography variant="h4">
                                        {title}
                                    </Typography>
                                </div>
                            </div> */}
              <div
                className="col-md-6 col-sm-12 text-end"
                style={{ display: 'flex', justifyContent: 'flex-end' }}
              >
                <button
                  className="btn btn-primary"
                  onClick={() => onSave(sections)}
                  disabled={loading}
                  style={{
                    backgroundColor: buttonColor || '#1976d2',
                    borderColor: buttonColor || '#1976d2',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                  }}
                >
                  {loading ? 'Saving...' : 'Save Settings'}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MainTextPage;
